/**
 * Manual light/dark override, layered on top of the CSS `prefers-color-scheme`
 * default (see the top-of-file comment in styles.css). The resolved theme is
 * written to `<html data-theme>`, which the dark-mode CSS blocks key off in
 * preference to the media query once a choice has been made.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';

/** localStorage throws in Safari private mode / when disabled by policy — degrade quietly, same pattern as auth/storage.ts. */
function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* the choice simply will not survive a reload */
  }
}

/** `window.matchMedia` is stubbed to never match in this project's JSDOM test environment (vitest.setup.ts) — resolves to light there. */
function systemTheme(): Theme {
  return typeof window !== 'undefined' && (window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false)
    ? 'dark'
    : 'light';
}

function initialTheme(): Theme {
  const stored = safeRead(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : systemTheme();
}

export interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    safeWrite(STORAGE_KEY, theme);
  }, [theme]);

  function toggleTheme(): void {
    setTheme((previous) => (previous === 'dark' ? 'light' : 'dark'));
  }

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === null) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
